import { FC, useCallback, useState } from 'react';

import { Box, Icon, Image, Text } from '@rbilabs/universal-components';
import { useRouter } from 'expo-router';
import { usePaymentContext } from 'features/payment';
import { IPaymentMethod } from 'features/payment/types';
import { useIntl } from 'react-intl';

import ActionButton from 'components/action-button';
import { CardModal } from 'components/card-modal';
import Currency from 'components/currency';
import { useCardNumber } from 'components/gift-card-modal/use-giftcard-number';
import { CurrentSelectedMethodType } from 'components/payment-method/types';
import { toaster } from 'components/toaster/toaster';
import { usePrepaidByNumber } from 'hooks/prepaid';
import useErrorModal from 'hooks/use-error-modal';
import { useLoyaltyUserId } from 'state/loyalty/hooks/use-loyalty-user';
import { primitive } from 'styles/constants/primitives';

import { AddGiftCardInput } from '../../add-gift-card-input';
import {
  BackgroundAndLogoContainer,
  GiftCardContainer,
  LogoAndNameContainer,
} from '../add-gift-card-redesign/styled';

import { TransferBalanceContainer } from './transfer-balance';

const crownsImg = require('../add-gift-card-redesign/Frame.png');

export const TransferGiftCardRedesign: FC = () => {
  const { formatMessage } = useIntl();

  const { loading: loyaltyUserLoading } = useLoyaltyUserId();
  const {
    giftCardNumber,
    errors,
    handleCardNumberChange,
    isValidGiftCardNumberCheck,
    validateCardNumber,
  } = useCardNumber();
  const {
    getPrepaidPaymentMethod,
    getBalanceFromPaymentMethods,
    mergePrepaidCardBalances,
    setCheckoutPaymentMethodId,
    setDefaultPaymentMethodId,
  } = usePaymentContext();
  const paymentMethod: IPaymentMethod | null = getPrepaidPaymentMethod();
  const currentAmount = (paymentMethod && getBalanceFromPaymentMethods(paymentMethod)) || 0;
  const { getPrepaidByNumber } = usePrepaidByNumber();
  const [cardBalance, setCardBalance] = useState(0);
  const [isCardLoading, setIsCardLoading] = useState(false);
  const [oldGiftCardNumber, setOldGiftCardNumber] = useState('');
  const [showBalance, setShowBalance] = useState(
    !isCardLoading && !errors.giftCardNumber && cardBalance > 0
  );
  // @ts-ignore
  const [ErrorDialog, openErrorDialog] = useErrorModal({
    modalAppearanceEventMessage: 'Error: Vaulting Gift Card',
  });

  const router = useRouter();

  const showGiftCardAddingErrorModal = useCallback(
    (error: unknown) => {
      // @ts-expect-error TS(2322) FIXME: Type 'unknown' is not assignable to type 'Error | ... Remove this comment to see the full error message
      openErrorDialog({ message: formatMessage({ id: 'giftCardAddingError' }), error });
    },
    [formatMessage, openErrorDialog]
  );

  const transferCard = useCallback(
    async (newGiftCardNumber: string) => {
      if (newGiftCardNumber) {
        const { data } = await getPrepaidByNumber({ cardNumber: newGiftCardNumber });
        if (!data) {
          showGiftCardAddingErrorModal(errors);
          setCardBalance(0);
        } else {
          if (data?.balance === 0) {
            openErrorDialog({ message: formatMessage({ id: 'giftCardAlreadyRedeem' }) });
          } else {
            setCardBalance(data?.balance || 0);
            setShowBalance(true);
          }
        }
        setIsCardLoading(false);
      }
    },
    [errors, formatMessage, getPrepaidByNumber, openErrorDialog, showGiftCardAddingErrorModal]
  );

  const handleGiftCardChange = useCallback(
    async (newGiftCardNumber: string) => {
      setIsCardLoading(true);
      handleCardNumberChange(newGiftCardNumber);
      validateCardNumber(newGiftCardNumber);

      if (isValidGiftCardNumberCheck(newGiftCardNumber)) {
        transferCard(newGiftCardNumber?.replace(/\s+/g, ''));
      } else if (errors) {
        setIsCardLoading(false);
      }
    },
    [errors, handleCardNumberChange, isValidGiftCardNumberCheck, transferCard, validateCardNumber]
  );

  const confirmTransferCard = async () => {
    setIsCardLoading(true);
    try {
      if (!isCardLoading && !errors.giftCardNumber && cardBalance > 0) {
        await mergePrepaidCardBalances({
          sourceCardNumber: giftCardNumber || '',
        });
        // Setting Default Payment method
        setCheckoutPaymentMethodId(CurrentSelectedMethodType.ADD_NEW_GIFT_CARD);
        setDefaultPaymentMethodId(CurrentSelectedMethodType.ADD_NEW_GIFT_CARD);
        router.dismiss();

        toaster.positive(formatMessage({ id: 'transferBalanceCompletedMessage' }));
      }
    } catch (error) {
      // @ts-expect-error TS(2322) FIXME: Type 'unknown' is not assignable to type 'Error | ... Remove this comment to see the full error message
      openErrorDialog({ message: formatMessage({ id: 'giftCardAddingError' }), error });
    } finally {
      setIsCardLoading(false);
    }
  };

  const showBalanceContainer = useCallback(() => {
    setOldGiftCardNumber(giftCardNumber);
    handleGiftCardChange(giftCardNumber);
  }, [giftCardNumber, handleGiftCardChange]);

  return (
    <CardModal
      standardPadding
      headerText={formatMessage({ id: 'transferGiftCard' })}
      footer={
        <ActionButton
          fullWidth
          onPress={showBalance ? confirmTransferCard : showBalanceContainer}
          isLoading={loyaltyUserLoading || isCardLoading}
        >
          {loyaltyUserLoading || isCardLoading ? null : formatMessage({ id: 'addToCart' })}
        </ActionButton>
      }
    >
      <GiftCardContainer>
        <BackgroundAndLogoContainer>
          <LogoAndNameContainer>
            <Icon accessibilityHidden variant="giftCard" size="xl" mb="$1" />
            <Text
              accessible={true}
              accessibilityRole="header"
              accessibilityLabel={formatMessage({ id: 'transferGiftCard' })}
              color={primitive.bk.$bbqBrown}
              fontFamily="heading"
              lineHeight="24px"
              fontSize="2xl"
              mb="$2"
            >
              {formatMessage({ id: 'giftCard' })}
            </Text>
            <Box width="full" flexDirection="row" mb="$1">
              <Text fontSize="sm">{formatMessage({ id: 'currentBalance' })}</Text>

              <Currency
                amount={currentAmount}
                textProps={{
                  margin: 0,
                  fontSize: 'sm',
                }}
              />
            </Box>
            {showBalance && (
              <Box width="full" flexDirection="row" mt="$1">
                <Text fontSize="sm" fontWeight="bold">
                  {formatMessage({ id: 'newBalance' })}
                </Text>

                <Currency
                  amount={currentAmount + cardBalance}
                  textProps={{
                    margin: 0,
                    fontSize: 'sm',
                  }}
                  isBold
                />
              </Box>
            )}
          </LogoAndNameContainer>
          <Image source={crownsImg} width={162} height={145} marginTop="$7" />
        </BackgroundAndLogoContainer>
      </GiftCardContainer>
      {!showBalance && (
        <AddGiftCardInput
          onChange={handleCardNumberChange}
          errorMessage={errors?.giftCardNumber}
          label={formatMessage({ id: 'cardNumber' })}
          testID="giftcard-input"
          initialValue={oldGiftCardNumber}
          isRedesign={true}
        />
      )}
      {showBalance && (
        <TransferBalanceContainer cardBalance={cardBalance} giftCardNumber={giftCardNumber} />
      )}

      <ErrorDialog />
    </CardModal>
  );
};
